<template>
  <cashflow-loader
    v-if="showContentLoader"
    :variant="otsJobConfig.flowConfigId"
  />
  <div
    v-else
    class="page"
  >
    <riseup-header
      :flow-config-id="otsJobConfig.flowConfigId"
      :is-green="currentPage === 1"
    />
    <div
      v-if="showLoader"
      class="loader-wrapper"
    >
      <tiny-loader
        class="loader-class"
        variant="discount"
      />
    </div>
    <div
      v-else
    >
      <connect-more-accounts
        v-if="creds.length > 0 || isPolling"
        :handle-click="handleClick"
        :connect-more-accounts="connectMoreAccountsClicked"
        :handle-back-click="handleBackClick"
      />
      <connect-accounts-landing
        v-else-if="currentPage === 1"
        :handle-click="handleClick"
      />
      <connect-true-layer
        v-else-if="currentPage === 2"
        :handle-click="handleClick"
      />
    </div>
  </div>
</template>

<script setup>
import { onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import RiseupHeader from '../Common/RiseupHeader.vue';
import CashflowLoader from './CashflowLoader.vue';
import Segment from '../../Segment';
import useContentLoader from '../../composables/useContentLoader';
import { authenticateTrueLayerCode, getTrueLayerUrl } from '../../api/trueLayer';
import { useSessionStore } from '../../stores/session';
import { _descopeAutheticate } from '../../utils/descopeUtils';
import { useInsights } from '../../services/insightsService';
import { getCustomerCredentials } from '../../api/cashflowApi';
import { useOtsJob } from '../../services/otsConfigService';
import { FlowConfigIdType } from '../../types/otsConfig';
import ConnectAccountsLanding from './ConnectAccounts/ConnectAccountsLanding.vue';
import ConnectTrueLayer from './ConnectAccounts/ConnectTrueLayer.vue';
import ConnectMoreAccounts from './ConnectAccounts/ConnectMoreAccounts.vue';

const sessionStore = useSessionStore();
const { isPolling, updateIsPolling, startPollingByJobId } = useInsights();

const { getOtsJobConfig } = useOtsJob();

const otsJobConfig = await getOtsJobConfig();

const router = useRouter();
const route = useRoute();
const { toggleLoader, showContentLoader } = useContentLoader();

Segment.identifyCustomer(sessionStore.customerId, sessionStore.memberId);

const creds = ref([]);
const showLoader = ref(false);

const currentPage = ref(0);

const handleClick = async () => {
  if (currentPage.value === 1) {
    currentPage.value = 2;
    Segment.trackUserGot('ConnectOtsAccountsGetStartedClicked');
  } else if (currentPage.value === 2) {
    currentPage.value = 3;
    Segment.trackUserGot('ConnectOtsAccountsConnectClicked');
    window.location.href = await getTrueLayerUrl({ jobId: otsJobConfig.jobId });
  } else if (currentPage.value === 3) {
    toggleLoader(true);
    startPollingByJobId(otsJobConfig.jobId, 300, 3000, handlePollingSuccess, handlePollingError);

    Segment.trackUserGot('ConnectOtsAccountsConnectMoreClicked');
  }
};

const connectMoreAccountsClicked = async () => {
  currentPage.value = 3;
  Segment.trackUserGot('ConnectOtsAccountsConnectMoreClicked');
  window.location.href = await getTrueLayerUrl({ jobId: otsJobConfig.jobId });
};

const handlePollingSuccess = () => {
  if (otsJobConfig.flowConfigId === FlowConfigIdType.Vanilla) {
    router.push('/ots/vanilla/success');
  } else if (otsJobConfig.flowConfigId === FlowConfigIdType.Custom) {
    router.push('/ots/cstm/success');
  } else {
    router.push('/ots/success');
  }
};

const handlePollingError = () => {
  throw new Error(`Could not get insights in time, ${sessionStore.customerId}`);
};

onBeforeMount(async () => {
  toggleLoader(false);
  showLoader.value = true;
  const queryParam = route.query || null;
  if (queryParam.token) {
    const isDescopeAutheticated = await _descopeAutheticate(queryParam.token);
    if (isDescopeAutheticated) {
      const sessionStore = useSessionStore();
      await sessionStore.init();
    }
  }

  creds.value = await getCustomerCredentials();

  if (queryParam.code) {
    updateIsPolling(true);
    const response = await authenticateTrueLayerCode(queryParam.code);
    if (response) {
      startPollingByJobId(otsJobConfig.jobId, 300, 3000, handlePollingSuccess, handlePollingError);
    } else {
      updateIsPolling(false);
    }
  }

  if (creds.value.length > 0 || isPolling.value) {
    currentPage.value = 3;
  } else {
    currentPage.value = 1;
  }
  showLoader.value = false;
  Segment.trackUserGot('EtoroConnectOtsAccountPageEntered');
});

const handleBackClick = () => {
  Segment.trackUserGot('ConnectOtsAccountsBackClicked');
  router.push('/ots-signup');
};

</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';

.page {
  background-color: $main_background_color;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

</style>
